import { ModalDelete, Tooltip } from '@repo/ui';
import FeatherIcon from 'feather-icons-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './_table-draggable-evaluation-criteria.css';

interface IPayloadProps {
  questionId: number;
  newOrder: number;
  optionId?: number;
}

interface IAction<T> {
  label: string;
  icon: string;
  color?: string;
  onClick: (row: T) => void;
}

interface ITableDraggableEvaluationCriteriaProps<T> {
  data: T[];
  columns: Array<{
    header: string;
    key: keyof T;
    render?: (row: T) => React.ReactNode;
  }>;
  handleEdit: (id: number) => void;
  handleDelete?: (id: string) => void;
  requestType: 'questions' | 'question-options';
  requestReorder?: (payload: IPayloadProps[]) => void;
  actions?: IAction<T>[];
}

export const TableDraggableEvaluationCriteria = <
  T extends {
    id?: number;
    type?: string;
    title?: string;
    questionId?: number;
  },
>({
  data,
  actions,
  columns,
  handleEdit,
  handleDelete,
  requestType,
  requestReorder,
}: ITableDraggableEvaluationCriteriaProps<T>) => {
  const [rows, setRows] = useState<T[]>(data);
  const [draggingRowIndex, setDraggingRowIndex] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<string | undefined>(undefined);

  const navigate = useNavigate();

  function toggleModalDelete(id?: string) {
    setRowToDelete(id);
    setIsDeleteModalOpen(!isDeleteModalOpen);
  }

  const handleDragStart = (index: number) => {
    setDraggingRowIndex(index);
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLTableRowElement>,
    index: number,
  ) => {
    e.preventDefault();
    if (draggingRowIndex !== null && draggingRowIndex !== index) {
      const updatedRows = [...rows];
      const draggedRow = updatedRows[draggingRowIndex];
      updatedRows.splice(draggingRowIndex, 1);
      updatedRows.splice(index, 0, draggedRow);
      setRows(updatedRows);
      setDraggingRowIndex(index);
    }
  };

  const generatePayload = (
    rows: T[],
    requestType: 'questions' | 'question-options',
  ) => {
    return rows.map((row, index) => {
      if (requestType === 'questions') {
        return {
          questionId: row.id,
          newOrder: index + 1,
        };
      }
      if (requestType === 'question-options') {
        return {
          optionId: row.id,
          questionId: row.questionId,
          newOrder: index + 1,
        };
      }
    });
  };

  const handleDragEnd = () => {
    setDraggingRowIndex(null);

    const payload = generatePayload(rows, requestType);
    requestReorder(payload);
  };

  useEffect(() => {
    setRows(data);
  }, [data]);

  return (
    <div className="table-responsive">
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key as string}>{column.header}</th>
            ))}
            <th> </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={draggingRowIndex === index ? 'dragging' : ''}
            >
              {columns.map((column) => (
                <td key={column.key as string}>
                  {column.render
                    ? column.render(row)
                    : String(row[column.key] ?? '-')}
                </td>
              ))}
              <td>
                <div className="actions">
                  {actions?.map((action, i) => {
                    const actionId = `action-${action.icon}-${row.id}`;
                    return (
                      <React.Fragment key={i}>
                        <i
                          id={actionId}
                          role="button"
                          onClick={() => action.onClick(row)}
                        >
                          <FeatherIcon
                            size={18}
                            icon={action.icon}
                            color={action.color || 'primary'}
                          />
                        </i>
                        <Tooltip targetId={actionId} text={action.label} />
                      </React.Fragment>
                    );
                  })}

                  {row.type === 'select' && (
                    <>
                      <i
                        id={`option-${row.id}`}
                        role="button"
                        onClick={() =>
                          navigate(
                            `/cadastros/instrumentos-de-avaliacao/opcoes/${row.id}`,
                            {
                              state: { title: row.title },
                            },
                          )
                        }
                      >
                        <FeatherIcon icon={'sliders'} size={18} />
                      </i>
                      <Tooltip targetId={`option-${row.id}`} text="Opção" />
                    </>
                  )}

                  <>
                    <i
                      id={`edit-${row.id}`}
                      role="button"
                      onClick={() => handleEdit(row.id)}
                    >
                      <FeatherIcon icon={'edit'} size={18} />
                    </i>
                    <Tooltip targetId={`edit-${row.id}`} text="Editar" />
                  </>

                  <>
                    <i
                      id={`delete-${row.id}`}
                      role="button"
                      onClick={() => toggleModalDelete(String(row.id))}
                    >
                      <FeatherIcon
                        icon={'minus-circle'}
                        size={18}
                        color="red"
                      />
                    </i>
                    <Tooltip targetId={`delete-${row.id}`} text="Excluir" />
                  </>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ModalDelete
        loading={false}
        isOpen={isDeleteModalOpen}
        handleDelete={() => {
          handleDelete(rowToDelete);
          toggleModalDelete();
        }}
        toggle={() => {
          setRowToDelete(undefined);
          toggleModalDelete();
        }}
      />
    </div>
  );
};
